import { useQuery, useMutation, useQueryClient } from "react-query";
import { useCallback, useMemo } from "react";
import {
  downloadProjectAttachment,
  getProjectAttachmentPreviews,
  uploadProjectAttachment,
  downloadRewardReceipt,
  getRewardReceiptPreviews,
  uploadRewardReceipt,
  getBackpackProjectAttachmentInformation,
  getRewardReceiptInformation,
} from "./projectAttachmentsApi";

type AttachmentType = "projects" | "rewards";

const serviceMap = {
  projects: {
    download: downloadProjectAttachment,
    getPreviews: getProjectAttachmentPreviews,
    getInformation: getBackpackProjectAttachmentInformation,
    upload: uploadProjectAttachment,
  },
  rewards: {
    download: downloadRewardReceipt,
    getPreviews: getRewardReceiptPreviews,
    getInformation: getRewardReceiptInformation,
    upload: uploadRewardReceipt,
  },
} as const;

export const PREVIEW_BASE_KEY = "attachment-preview";
export const useAttachmentPreviewQuery = (
  project_id: number,
  type: AttachmentType,
  reward_id?: number
) => {
  const getPreviews = serviceMap[type].getPreviews;
  const getInformation = serviceMap[type].getInformation;

  const handleGetPreviews = useCallback(async () => {
    if (type === "projects") {
      return await getPreviews(project_id);
    }
    if (type === "rewards") {
      if (!reward_id) return {};
      return await getPreviews(reward_id);
    }
    return {};
  }, [getPreviews, project_id, reward_id, type]);

  const baseQueryKey = [PREVIEW_BASE_KEY, type, project_id];
  const queryKey =
    type === "rewards" ? [...baseQueryKey, reward_id] : baseQueryKey;
  return useQuery(queryKey, async () => {
    const images = await handleGetPreviews();
    // this will be updated to reward_id once a reward needs to exist pre-upload in a later release
    const names = await getInformation(project_id);
    const allFileNames = names.map((name) => name.filename);
    return names.map((name) => ({
      ...name,
      preview: images[name.id.toString()],
      hasDuplicate:
        name.version > 1 ||
        allFileNames.filter((n) => n === name.filename).length > 1,
    }));
  });
};

export const useAttachmentUploadMutation = (
  projectId: number,
  type: AttachmentType
) => {
  const upload = useMemo(() => serviceMap[type].upload, [type]);
  const queryClient = useQueryClient();
  return useMutation((file: File) => upload(projectId, file), {
    onSuccess: () =>
      queryClient.invalidateQueries([PREVIEW_BASE_KEY, type, projectId]),
  });
};
